import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { EventCard } from "@entities/event-card";

// import { MONTHS_RUS } from "@shared/constants";
import { Button } from "@shared/ui/button";
import { useScroll } from "@shared/ui/scroll/scroll";
import { Body, Heading } from "@shared/ui/typography";
import { currentDate } from "@shared/utils/get-current-date";

import type { EventType } from "@/_pages/event/model";

import { rebuildEvents } from "../../utils/rebuild-data";

import s from "./all-events.module.scss";

export type CompProps = ComponentProps<"div"> & {
  events: EventType[];
};

export const AllEvents = (props: CompProps) => {
  const { events } = props;

  const scroll = useScroll();

  const rebuildedData = useMemo(() => {
    return rebuildEvents(events);
  }, [events]);

  const [activeMouth, setActiveMouth] = useState({
    year: rebuildedData[0]?.year,
    month: rebuildedData[0]?.months[0]?.month.toLowerCase(),
  });

  const currentEvents = useMemo(() => {
    const currentYear = rebuildedData.find(
      (item) => item.year === activeMouth.year,
    );

    const currentMonth = currentYear?.months.find(
      (item) => item.month === activeMouth.month,
    );

    const eventsByMonth = currentMonth?.events ?? [];

    return [...eventsByMonth].sort((a, b) => {
      const aTime = a?.date
        ? new Date(a.date).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bTime = b?.date
        ? new Date(b.date).getTime()
        : Number.MAX_SAFE_INTEGER;

      return aTime - bTime;
    });
  }, [activeMouth, rebuildedData]);

  const handleClickOnMonth = useCallback(
    (year: string, month: string) => {
      setActiveMouth({
        year,
        month,
      });

      scroll?.scrollTo("#all-events", { offset: -200 });
    },
    [scroll],
  );

  return (
    <section className={clsx(s.root)}>
      <Heading level={3} tag="h2" className={s.title}>
        Предстоящие события
      </Heading>
      <div className={s.content} id="all-events">
        <div className={s.navigation}>
          {(rebuildedData ?? []).map((yearEvents, yearIdx) =>
            yearEvents ? (
              <div
                key={`year-${yearEvents?.year ?? yearIdx}`}
                className={s.yearSection}
              >
                {currentDate("year") !== yearEvents?.year && (
                  <Body
                    size="caption"
                    weight="semiBold"
                    className={s.yearTitle}
                  >
                    {yearEvents.year}
                  </Body>
                )}
                <div className={s.section}>
                  {(yearEvents?.months ?? []).map((item, monthIdx) =>
                    item ? (
                      <Button
                        key={item?.month ?? monthIdx}
                        className={clsx(s.button, {
                          [s.activeMounth]:
                            `${activeMouth.year}-${activeMouth.month}` ===
                            `${yearEvents?.year}-${item?.month?.toLowerCase() ?? ""}`,
                        })}
                        onClick={() =>
                          handleClickOnMonth(
                            yearEvents.year,
                            item?.month?.toLowerCase() ?? "",
                          )
                        }
                      >
                        <Heading
                          level={6}
                          tag={"p"}
                          data-label={item?.events?.length ?? 0}
                          className={s.month}
                        >
                          {item?.month ?? ""}
                        </Heading>
                      </Button>
                    ) : null,
                  )}
                </div>
              </div>
            ) : null,
          )}
        </div>
        <div className={s.cards}>
          {(currentEvents ?? []).map((event, eventIdx) =>
            event ? (
              <EventCard
                key={event?.documentId ?? event?.slug ?? eventIdx}
                event={event}
              />
            ) : null,
          )}
        </div>
      </div>
    </section>
  );
};

AllEvents.displayName = "AllEvents";
